import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

// Shared by cross-organisation record lists; all in the URL so a filtered list survives reload and can be shared.
export interface RecordListFilterValues<S extends string> {
  organisationId?: string;
  state?: S;
  text?: string;
  /** Start of the `from` day, UTC, ISO. */
  from?: string;
  /** End of the `to` day, UTC, ISO: the range includes the whole of its last day. */
  to?: string;
}

interface RecordListFiltersConfig<S extends string> {
  states: Record<S, string>;
  /** URL parameter for the text filter, e.g. "invoiceNumber" or "merchant". */
  textParam: string;
}

export function useRecordListFilters<S extends string>({ states, textParam }: RecordListFiltersConfig<S>) {
  const [searchParams, setSearchParams] = useSearchParams();

  const [organisationIdInput, setOrganisationIdInput] = useState(searchParams.get("organisationId") ?? "");
  const [textInput, setTextInput] = useState(searchParams.get(textParam) ?? "");

  const debouncedOrganisationId = useDebouncedValue(organisationIdInput.trim());
  const debouncedText = useDebouncedValue(textInput.trim());

  useEffect(() => {
    setSearchParams(
      (params) => {
        setOrDelete(params, "organisationId", debouncedOrganisationId);
        setOrDelete(params, textParam, debouncedText);
        return params;
      },
      { replace: true },
    );
  }, [debouncedOrganisationId, debouncedText, textParam, setSearchParams]);

  function updateParam(key: string, value: string | null) {
    setSearchParams((params) => setOrDelete(params, key, value), { replace: true });
  }

  const stateParam = searchParams.get("state");
  const state = stateParam && stateParam in states ? (stateParam as S) : undefined;
  const fromDate = validDate(searchParams.get("from"));
  const toDate = validDate(searchParams.get("to"));

  // Only apply the organisation filter once it's a full UUID - the API expects uuid format.
  const organisationId = UUID_PATTERN.test(debouncedOrganisationId) ? debouncedOrganisationId : undefined;

  const values: RecordListFilterValues<S> = {
    organisationId,
    state,
    text: debouncedText || undefined,
    from: fromDate ? `${fromDate}T00:00:00.000Z` : undefined,
    to: toDate ? `${toDate}T23:59:59.999Z` : undefined,
  };

  return {
    values,
    bar: {
      states,
      organisationIdInput,
      setOrganisationIdInput,
      organisationIdInvalid: !!debouncedOrganisationId && !organisationId,
      textInput,
      setTextInput,
      state,
      setState: (value: S | undefined) => updateParam("state", value ?? null),
      fromDate,
      setFromDate: (value: string) => updateParam("from", value),
      toDate,
      setToDate: (value: string) => updateParam("to", value),
    },
  };
}

type FilterBarState<S extends string> = ReturnType<typeof useRecordListFilters<S>>["bar"];

interface RecordListFilterBarProps<S extends string> {
  filters: FilterBarState<S>;
  textPlaceholder: string;
  /** What the date range is measured on, e.g. "Issue date". */
  dateLabel: string;
}

export function RecordListFilterBar<S extends string>({
  filters,
  textPlaceholder,
  dateLabel,
}: RecordListFilterBarProps<S>) {
  const stateValues = Object.keys(filters.states) as S[];

  return (
    <div className="mb-4 flex flex-wrap items-center gap-3">
      <Input
        placeholder="Filter by organisation ID..."
        value={filters.organisationIdInput}
        onChange={(e) => filters.setOrganisationIdInput(e.target.value)}
        aria-invalid={filters.organisationIdInvalid}
        spellCheck={false}
        autoComplete="off"
        className="w-80 font-mono text-xs"
      />
      <Input
        placeholder={textPlaceholder}
        value={filters.textInput}
        onChange={(e) => filters.setTextInput(e.target.value)}
        spellCheck={false}
        autoComplete="off"
        className="w-64"
      />
      <Select
        value={filters.state ?? "any"}
        onValueChange={(v) => filters.setState(v === "any" ? undefined : (v as S))}
      >
        <SelectTrigger className="w-44">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="any">All states</SelectItem>
          {stateValues.map((value) => (
            <SelectItem key={value} value={value}>
              {filters.states[value]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <span>{dateLabel}</span>
        <Input
          type="date"
          aria-label={`${dateLabel} from`}
          value={filters.fromDate ?? ""}
          max={filters.toDate}
          onChange={(e) => filters.setFromDate(e.target.value)}
          className="w-36 dark:[color-scheme:dark]"
        />
        <span>to</span>
        <Input
          type="date"
          aria-label={`${dateLabel} to`}
          value={filters.toDate ?? ""}
          min={filters.fromDate}
          onChange={(e) => filters.setToDate(e.target.value)}
          className="w-36 dark:[color-scheme:dark]"
        />
      </div>
    </div>
  );
}

function setOrDelete(params: URLSearchParams, key: string, value: string | null | undefined) {
  if (value) params.set(key, value);
  else params.delete(key);
  return params;
}

function validDate(value: string | null): string | undefined {
  return value && DATE_PATTERN.test(value) ? value : undefined;
}
